import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectFeatureFlags } from 'core/userContext/selectors';
import { AddNew16 } from 'ui/icons/aurora/16px/AddNew16';
import { Export16 } from 'ui/icons/aurora/16px/Export16';
import { PrimaryButton, SecondaryButton, BUTTON_SIZES } from 'ui/button';
import { PageHeader } from 'ui/pageHeader';
import { colors, spacing } from 'ui/theme';
import { HelpFooter } from 'ui/helpFooter';
import { InfoBanner } from 'ui/banner';
import { SortingContextProvider } from 'utils/contexts/sorting';
import { ModalContextProvider } from 'utils/contexts/modal';
import { pxToRem } from 'utils/convert';
import {
  DrawersContextProvider,
  FiltersContextProvider,
  CustomEventsQueryContextProvider,
  SelectionContextProvider,
  useFiltersContext,
  useModalsContext,
  useCustomEventsQueryContext,
} from '../contexts';
import { useExportCustomEvents } from '../hooks';
import { INGESTION_POLICIES } from '../types';
import { configuredIngestionPolicy } from '../utils';
import { CustomEventsFilterBar } from './CustomEventsFilterBar';
import { CustomEventsHeaderOverflowMenu } from './CustomEventsHeaderOverflowMenu';
import { CustomEventsTable } from './CustomEventsTable';
import { ReferencesDrawer } from './ReferencesDrawer';
import { UpdateCustomEventSettingsModals } from './UpdateCustomEventSettingsModals';

export const TEXT = {
  EXPORT_CSV_BUTTON: 'Export event summary to CSV',
  FOOTER_HREF: 'https://support.iterable.com/hc/en-us/articles/360043366492',
  FOOTER_TEXT: 'custom events',
  NEW_CUSTOM_EVENT_BUTTON: 'New custom event',
  PAGE_HEADER: 'Custom Events',
  INFO_BANNER_TITLE: 'All new custom events are currently being blocked',
  INFO_BANNER_BODY:
    'Existing custom events will continue to be stored, but new custom events will be blocked from this project.',
  INFO_BANNER_ACTIONS_BUTTON_TEXT: 'Allow new custom events',
};

const InfoBannerWrapper = styled.div`
  margin-bottom: ${spacing.plusHalf};
  min-width: ${pxToRem(640)};
`;

const PageActionsWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  gap: ${spacing.half};
`;

export const CustomEventsPage = () => {
  const { showAddModal, showAllowModal } = useModalsContext();
  const { customEvents, allowNewCustomEvents } = useCustomEventsQueryContext();
  const shouldShowEventsBlockedBanner = allowNewCustomEvents === false;

  const { CustomEventsPageModernized } = useSelector(selectFeatureFlags);
  const { ingestionPolicyFilter, searchQuery, setIngestionPolicyFilter } = useFiltersContext();

  // Search/filter by event name

  const filteredCustomEvents = customEvents.filter(
    (event) =>
      (ingestionPolicyFilter === INGESTION_POLICIES.ALL ||
        configuredIngestionPolicy(event) === ingestionPolicyFilter) &&
      event.eventName.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const exportCustomEventsCallback = useExportCustomEvents(filteredCustomEvents);

  return (
    <>
      <PageHeader
        pageActions={
          <PageActionsWrapper>
            {CustomEventsPageModernized ? (
              <>
                <CustomEventsHeaderOverflowMenu
                  allowNewCustomEvents={allowNewCustomEvents as boolean}
                  filteredCustomEvents={filteredCustomEvents}
                />
                <PrimaryButton size={BUTTON_SIZES.LARGE} onClick={showAddModal}>
                  <AddNew16 />
                  {TEXT.NEW_CUSTOM_EVENT_BUTTON}
                </PrimaryButton>
              </>
            ) : (
              <SecondaryButton onClick={() => exportCustomEventsCallback()}>
                <Export16 color={colors.textSecondary} />
                <span>{TEXT.EXPORT_CSV_BUTTON}</span>
              </SecondaryButton>
            )}
          </PageActionsWrapper>
        }
      >
        {TEXT.PAGE_HEADER}
      </PageHeader>

      {CustomEventsPageModernized && shouldShowEventsBlockedBanner && (
        <InfoBannerWrapper>
          <InfoBanner
            title={TEXT.INFO_BANNER_TITLE}
            BannerActions={
              <SecondaryButton onClick={showAllowModal}>
                {TEXT.INFO_BANNER_ACTIONS_BUTTON_TEXT}
              </SecondaryButton>
            }
          >
            {TEXT.INFO_BANNER_BODY}
          </InfoBanner>
        </InfoBannerWrapper>
      )}

      <CustomEventsFilterBar />

      <SortingContextProvider
        defaultSortField="eventName"
        defaultSortDirection={SORT_DIRECTIONS.ASCENDING}
      >
        <CustomEventsTable customEvents={filteredCustomEvents} />
      </SortingContextProvider>

      <UpdateCustomEventSettingsModals setIngestionPolicyFilter={setIngestionPolicyFilter} />

      <ReferencesDrawer />

      <HelpFooter href={TEXT.FOOTER_HREF} text={TEXT.FOOTER_TEXT} />
    </>
  );
};

export default () => (
  <CustomEventsQueryContextProvider>
    <FiltersContextProvider>
      <SelectionContextProvider>
        <ModalContextProvider>
          <DrawersContextProvider>
            <CustomEventsPage />
          </DrawersContextProvider>
        </ModalContextProvider>
      </SelectionContextProvider>
    </FiltersContextProvider>
  </CustomEventsQueryContextProvider>
);
